import * as Sentry from '@sentry/browser';
import React, {useEffect, useReducer, useRef} from 'react';
import {useIntl, type IntlShape} from 'react-intl';
import styled from 'styled-components';
import {Alert, Button, EMPHASIS, ListCard, Tooltip, WorkSignifier} from '@forecast-it/design-system';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import {
	AGENT_RUN_STATE,
	AGENT_RUN_STATE_REASON,
	type AgentDeclaration,
	type AgentLastRun,
	type AgentPage,
	type AgentRecord,
	type AgentResult,
} from './agentDeclaration';
import {ensureAgentsRegistered, getAgentsForPage} from './agentRegistry';
import {useAgentPanelState} from './agentRunState';
import {canSeeAgent} from './agentVisibility';
import {agentControlDataCy} from './AgentToolbarButtons';

ensureAgentsRegistered();

export const RUN_JUST_NOW_MINUTES = 5;
// The server fails a run that is still going after this long.
const RUN_TIME_LIMIT_MINUTES = 20;
const MILLISECONDS_PER_MINUTE = 60 * 1000;
const PAGE_SIDE_MARGIN_PIXELS = 24;
const PANEL_GAP_PIXELS = 16;
const KEY_SEPARATOR = '#';
const RUN_DATE_FORMAT: Intl.DateTimeFormatOptions = {
	day: 'numeric',
	month: 'short',
};

const ROW_ACTION = {
	DELETE: 'DELETE',
	HIDE: 'HIDE',
} as const;
type RowAction = (typeof ROW_ACTION)[keyof typeof ROW_ACTION];

// The row whose Delete or Hide was pressed. The pressed button leaves with the row.
type LeavingRow = {
	resultId: string;
	index: number;
	action: RowAction;
};

const PanelMargin = styled.div`
	margin: 0 ${PAGE_SIDE_MARGIN_PIXELS}px ${PANEL_GAP_PIXELS}px;
`;

const actionButtonKey = (resultId: string, action: RowAction): string => `${resultId}${KEY_SEPARATOR}${action}`;

const recordKey = (recordIndex: number, record: AgentRecord): string => `${recordIndex}${KEY_SEPARATOR}${record.label}`;

const focusAgentControl = (agentId: string): void => {
	document.querySelector<HTMLElement>(`[data-cy="${agentControlDataCy(agentId)}"] button`)?.focus();
};

export const formatRunTime = (producedAt: string | null, now: Date, intl: IntlShape): string | null => {
	if (producedAt === null) {
		return null;
	}
	const finishedAt = new Date(producedAt);
	if (now.getTime() - finishedAt.getTime() < RUN_JUST_NOW_MINUTES * MILLISECONDS_PER_MINUTE) {
		return intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.PANEL_RUN_JUST_NOW});
	}
	const isSameDay = finishedAt.toDateString() === now.toDateString();
	return intl.formatMessage(
		{id: AGENTIC_WORKERS_MESSAGE.PANEL_RUN_AT},
		{time: isSameDay ? intl.formatTime(finishedAt) : intl.formatDate(finishedAt, RUN_DATE_FORMAT)}
	);
};

const describeFailure = (declaration: AgentDeclaration, failedRun: AgentLastRun, intl: IntlShape): string => {
	const agentReason = declaration.failureReason(
		{
			state: AGENT_RUN_STATE.FAILED,
			stateReason: failedRun.stateReason,
			results: [],
			detail: failedRun.detail,
		},
		intl
	);
	const reason =
		agentReason ??
		(failedRun.stateReason === AGENT_RUN_STATE_REASON.TIMED_OUT
			? intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.FAILED_REASON_TIMED_OUT}, {minutes: RUN_TIME_LIMIT_MINUTES})
			: intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.FAILED_REASON_UNKNOWN}));
	return `${intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.FAILED_TITLE})} ${reason}`;
};

// One agent's panel. A component of its own because each agent holds its own run state.
const AgentPanelForAgent = ({declaration}: {declaration: AgentDeclaration}): JSX.Element | null => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {visibleResults, failedRun, review, deleteResult, hideResult, retry} = useAgentPanelState(declaration);
	const [, showTimePassing] = useReducer((ticks: number) => ticks + 1, 0);
	const actionButtonsRef = useRef(new Map<string, HTMLButtonElement>());
	const retryButtonRef = useRef<HTMLButtonElement>(null);
	const leavingRowRef = useRef<LeavingRow | null>(null);
	const isRetryingRef = useRef(false);
	const isListShowing = visibleResults.length > 0;

	// Nothing in the store changes as a run grows older, so the date lines are rendered again every minute.
	useEffect(() => {
		if (!isListShowing) {
			return undefined;
		}
		const interval = setInterval(showTimePassing, MILLISECONDS_PER_MINUTE);
		return () => clearInterval(interval);
	}, [isListShowing]);

	// A pressed button that leaves with its row or card hands focus on, instead of letting it fall back to the page.
	useEffect(() => {
		const leavingRow = leavingRowRef.current;
		if (leavingRow && !visibleResults.some(result => result.id === leavingRow.resultId)) {
			leavingRowRef.current = null;
			if (isListShowing) {
				const nextResult = visibleResults[Math.min(leavingRow.index, visibleResults.length - 1)];
				actionButtonsRef.current.get(actionButtonKey(nextResult.id, leavingRow.action))?.focus();
			} else if (failedRun) {
				retryButtonRef.current?.focus();
			} else {
				focusAgentControl(declaration.id);
			}
		}
		if (isRetryingRef.current && failedRun === null) {
			isRetryingRef.current = false;
			focusAgentControl(declaration.id);
		}
	});

	const keepActionButton =
		(resultId: string, action: RowAction) =>
		(button: HTMLButtonElement | null): void => {
			const key = actionButtonKey(resultId, action);
			if (button) {
				actionButtonsRef.current.set(key, button);
			} else {
				actionButtonsRef.current.delete(key);
			}
		};

	const pressRowAction = (result: AgentResult, index: number, action: RowAction): void => {
		leavingRowRef.current = {
			resultId: result.id,
			index,
			action,
		};
		const command = action === ROW_ACTION.DELETE ? deleteResult : hideResult;
		command(result);
	};

	const pressRetry = (): void => {
		isRetryingRef.current = true;
		retry();
	};

	const now = new Date();
	const reviewLabel = formatMessage({id: AGENTIC_WORKERS_MESSAGE.PANEL_REVIEW});
	const deleteLabel = formatMessage({id: AGENTIC_WORKERS_MESSAGE.PANEL_DELETE});
	const hideTip = formatMessage({id: AGENTIC_WORKERS_MESSAGE.PANEL_HIDE});
	const acceptLabelOf = (result: AgentResult): string =>
		result.rows.length === 1
			? formatMessage(declaration.acceptLabel.single)
			: formatMessage(declaration.acceptLabel.bulk, {count: result.rows.length});
	const dateLineOf = (result: AgentResult, agentDateLine: string): string => {
		const runTime = formatRunTime(result.producedAt, now, intl);
		return runTime === null
			? agentDateLine
			: formatMessage(
					{id: AGENTIC_WORKERS_MESSAGE.PANEL_DATE_LINE},
					{
						runTime,
						dateLine: agentDateLine,
					}
			  );
	};

	// The live region stays mounted, empty and without height, while the agent is visible: screen readers often don't
	// announce a region that arrives already filled.
	return (
		<>
			<div role={'status'}>
				{failedRun && (
					<PanelMargin>
						<Alert type={Alert.TYPE.ERROR} text={describeFailure(declaration, failedRun, intl)}>
							<Button ref={retryButtonRef} emphasis={EMPHASIS.MEDIUM} onPress={pressRetry}>
								{formatMessage({id: AGENTIC_WORKERS_MESSAGE.FAILED_RETRY})}
							</Button>
						</Alert>
					</PanelMargin>
				)}
			</div>
			{isListShowing && (
				<PanelMargin>
					<ListCard
						aria-label={formatMessage(
							{id: AGENTIC_WORKERS_MESSAGE.PANEL_LABEL},
							{
								name: formatMessage(declaration.name),
								resultPlural: formatMessage(declaration.resultNoun.plural),
							}
						)}
						title={formatMessage(
							{id: AGENTIC_WORKERS_MESSAGE.PANEL_TITLE},
							{
								count: visibleResults.length,
								resultSingular: formatMessage(declaration.resultNoun.singular),
								resultPlural: formatMessage(declaration.resultNoun.plural),
							}
						)}
					>
						{visibleResults.map((result, index) => {
							const content = declaration.describeResult(result, intl);
							return (
								<ListCard.Row
									key={result.id}
									leading={content.records.map((record, recordIndex) => (
										<WorkSignifier
											key={recordKey(recordIndex, record)}
											workId={record.label}
											name={record.name}
											color={record.color}
										/>
									))}
									title={content.title}
									subtitle={dateLineOf(result, content.dateLine)}
									value={content.total}
									actions={
										<>
											<Button emphasis={EMPHASIS.LOW} onPress={() => review(result)}>
												{reviewLabel}
											</Button>
											<Button emphasis={EMPHASIS.MEDIUM} disabled>
												{acceptLabelOf(result)}
											</Button>
											<Button
												ref={keepActionButton(result.id, ROW_ACTION.DELETE)}
												emphasis={EMPHASIS.LOW}
												onPress={() => pressRowAction(result, index, ROW_ACTION.DELETE)}
											>
												{deleteLabel}
											</Button>
											<Tooltip content={hideTip}>
												<Button
													ref={keepActionButton(result.id, ROW_ACTION.HIDE)}
													emphasis={EMPHASIS.LOW}
													icon={'close'}
													aria-label={hideTip}
													onPress={() => pressRowAction(result, index, ROW_ACTION.HIDE)}
												/>
											</Tooltip>
										</>
									}
								/>
							);
						})}
					</ListCard>
				</PanelMargin>
			)}
		</>
	);
};

type AgentPanelBoundaryProps = {
	children: React.ReactNode;
};

type AgentPanelBoundaryState = {
	hasFailed: boolean;
};

// The panel runs the agent's own code as it renders, so an agent that throws takes down only its own panel, not the
// page around it.
class AgentPanelBoundary extends React.Component<AgentPanelBoundaryProps, AgentPanelBoundaryState> {
	static getDerivedStateFromError(): AgentPanelBoundaryState {
		return {
			hasFailed: true,
		};
	}

	state: AgentPanelBoundaryState = {
		hasFailed: false,
	};

	componentDidCatch(error: Error): void {
		Sentry.captureException(error);
	}

	render(): React.ReactNode {
		if (this.state.hasFailed) {
			return null;
		}
		return this.props.children;
	}
}

type AgentPanelProps = {
	page: AgentPage;
};

export const AgentPanel = ({page}: AgentPanelProps): JSX.Element | null => {
	const visibleAgents = getAgentsForPage(page).filter(canSeeAgent);
	if (visibleAgents.length === 0) {
		return null;
	}
	return (
		<>
			{visibleAgents.map(declaration => (
				<AgentPanelBoundary key={declaration.id}>
					<AgentPanelForAgent declaration={declaration} />
				</AgentPanelBoundary>
			))}
		</>
	);
};
